import {
  PRODUCT_LIST_SEPARATOR,
  buildProductListFromItems,
  formatSettlementItemToken,
} from "@/utils/shippingProductList";

// 배송정보 상품명 stale 분류 — 순수 함수 (플랜 codexloop 3R 합의, 2026-07-29).
//
// 입력은 order_item "원시 행"(빈 이름 포함)이다. 합성은 shippingProductList의 단일 소스를
// 그대로 재사용하고, 분류는 그 위에서만 판단한다. 규칙 요약:
//   · ok                    — stored == 합성값, 또는 (가드 하에) 순열 동치. stale 아님.
//   · prefix_compatible     — stored가 현재 항목들의 정확한 k-prefix 합성값(0<k<n).
//                             기본 체크는 suffix 규칙(아래) 통과 시에만.
//   · other_difference      — 그 외 불일치. 수기 편집 가능성 → 기본 해제.
//   · blank                 — stored가 빈 값. 의도적 공백 가능성 → 기본 해제.
//   · zero_item_with_stored — 합성 가능한 항목이 없는데 stored 잔존. 쓰기 불가, 경고 표시만.
//   · not_applicable        — 항목도 stored도 없음. 목록 제외.
//
// suffix 기본 체크 규칙(휴리스틱 — provenance가 아니다):
//   미매칭 suffix "전원"의 created_at이 parsed_order.modified_at보다 이후 = 마지막 배송정보
//   쓰기 이후 생긴 항목만 추가 제안. 백테스트(30일): k-prefix 178건 중 162건(91%) 체크 유지.
//   max(modified_at) 비교는 가격-만 수정(합성 무관)에 오염되어 기각됨 — created_at은 불변이라 안전.
//   timestamp 결측/파싱 실패 = fail-closed(해제). 수기 축약을 "정책"으로 쓰는 셀러가 이후
//   실제 추가를 받는 복합 케이스는 timestamp로 복원 불가 — 문서화된 잔존 리스크.
//
// 순열 비교 가드: 합성 토큰에 구분자(", ")가 포함된 항목이 하나라도 있으면 stored를 분해해
// 경계를 복원할 수 없다(분해 자체가 프리픽스 오탐의 원인이었음) → 순열 비교를 시도하지 않고
// other_difference로 보수 폴백. 재정렬+추가 복합도 other_difference로만 잡힌다(커버리지 한계).

export interface OrderItemForSync {
  id: number;
  item_name: string | null;
  quantity: number | null;
  created_at?: string | null;
}

export type StaleClassification =
  | { kind: "ok" }
  | { kind: "not_applicable" }
  | { kind: "zero_item_with_stored" }
  | { kind: "blank"; synthesized: string }
  | { kind: "prefix_compatible"; synthesized: string; defaultChecked: boolean }
  | { kind: "other_difference"; synthesized: string };

// RPC expectedItems 계약: 포맷 필터와 무관한 "원시 전 행", id ASC, 정확히 이 3키.
// 서버(apply_shipping_product_name_sync)의 jsonb_build_object('id','item_name','quantity')와
// 구조 등가 비교되므로 키 추가/누락 금지.
export interface ExpectedItemTuple {
  id: number;
  item_name: string | null;
  quantity: number | null;
}

export const buildExpectedItemsPayload = (items: OrderItemForSync[]): ExpectedItemTuple[] =>
  [...items]
    .sort((a, b) => a.id - b.id)
    .map((i) => ({ id: i.id, item_name: i.item_name ?? null, quantity: i.quantity ?? null }));

// timestamp 비교: PostgREST ISO 문자열을 ms 정밀도로 파싱. 어느 한쪽이라도 파싱 불가면
// null 반환 → 호출부 fail-closed. (µs는 Date에서 절사 — 동일 ms로 절사되면 '>'가 false = 안전 방향.)
const parseTimeMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

const isSuffixAllCreatedAfter = (
  suffix: OrderItemForSync[],
  parsedModifiedAt: string | null | undefined
): boolean => {
  const baseMs = parseTimeMs(parsedModifiedAt);
  if (baseMs === null) return false;
  return suffix.every((item) => {
    const createdMs = parseTimeMs(item.created_at);
    return createdMs !== null && createdMs > baseMs;
  });
};

const multisetEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const counts = new Map<string, number>();
  for (const s of a) counts.set(s, (counts.get(s) ?? 0) + 1);
  for (const s of b) {
    const c = counts.get(s);
    if (!c) return false;
    if (c === 1) counts.delete(s);
    else counts.set(s, c - 1);
  }
  return counts.size === 0;
};

export function classifyShippingStale(params: {
  stored: string | null;
  parsedModifiedAt: string | null;
  items: OrderItemForSync[];
}): StaleClassification {
  const { stored, parsedModifiedAt, items } = params;

  // id ASC 고정 — 조회가 정렬을 보장하더라도 분류의 전제라 여기서 다시 강제한다.
  const ordered = [...items].sort((a, b) => a.id - b.id);

  // 합성에 실제 참여하는(비공백 이름) 항목만, 순서 유지. suffix 규칙도 이 목록 기준(합의사항).
  const retained = ordered.filter(
    (i) => formatSettlementItemToken({ name: i.item_name, quantity: i.quantity }).length > 0
  );
  const tokens = retained.map((i) =>
    formatSettlementItemToken({ name: i.item_name, quantity: i.quantity })
  );
  const synthesized = buildProductListFromItems(
    ordered.map((i) => ({ name: i.item_name, quantity: i.quantity }))
  );

  const storedValue = stored ?? "";
  const storedBlank = storedValue.trim().length === 0;

  if (tokens.length === 0) {
    return storedBlank ? { kind: "not_applicable" } : { kind: "zero_item_with_stored" };
  }
  if (storedBlank) {
    return { kind: "blank", synthesized };
  }
  if (storedValue === synthesized) {
    return { kind: "ok" };
  }

  // 정확한 k-prefix (0<k<n): 누적 합성값과의 완전 일치만 인정 — 원시 startsWith는
  // 상품명 내 구분자 포함 케이스에서 오탐(구분자 미이스케이프)이라 금지.
  let prefix = "";
  for (let k = 1; k < tokens.length; k++) {
    prefix = k === 1 ? tokens[0] : `${prefix}${PRODUCT_LIST_SEPARATOR}${tokens[k - 1]}`;
    if (prefix === storedValue) {
      const suffix = retained.slice(k);
      return {
        kind: "prefix_compatible",
        synthesized,
        defaultChecked: isSuffixAllCreatedAfter(suffix, parsedModifiedAt),
      };
    }
  }

  // 순열 동치(재정렬 유래) → stale 아님. 단 어떤 토큰이든 구분자를 포함하면 분해 불가 → 시도 금지.
  const anyTokenHasSeparator = tokens.some((t) => t.includes(PRODUCT_LIST_SEPARATOR));
  if (!anyTokenHasSeparator) {
    const storedParts = storedValue.split(PRODUCT_LIST_SEPARATOR);
    if (multisetEqual(storedParts, tokens)) {
      return { kind: "ok" };
    }
  }

  return { kind: "other_difference", synthesized };
}
